import { useQueryClient, useMutation } from "@tanstack/react-query";
import { CustomerQueryKey } from "api/customer.api";
import { deleteFacility } from "api/facility.api";
import DangerModal from "components/DangerModal";
import { DotMenu } from "components/DotMenu";
import {
  ShadowCard,
  HeaderContainer,
  ContentContainer,
} from "components/ShadowCard";
import { Headline4 } from "components/typography/Headline4";
import { useNotification } from "hooks/useNotification";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Facility } from "types/facility.type";
import { GetKommunLabel } from "types/Kommun.type";
import { PatchFacilityModal } from "../customer/AddFacility/PatchFacilityModal";
import { usePatchFacilityLogic } from "../customer/AddFacility/usePatchFacilityLogic.hook";
import {
  DetailsList,
  Item,
  Label,
  Value,
} from "components/detailsList/DetailsList";
import { useIsAdmin } from "store/user.store";
import { OperatorPrivilege } from "types/company.type";
import { useHasRightsForFeature } from "api/company.api";
import { useAddUppdragFromFacilityPage } from "../uppdrag/add-uppdrag-modal/useAddUppdragFromFacilityPage";
import { AddUppdragFromFacilityPageModal } from "../uppdrag/add-uppdrag-modal/AddUppdragFromFacilityPageModal";
import PrimaryButton from "components/buttons/PrimaryButton";

type FacilityProps = {
  facility: Facility;
};
export const FacilityDetailsTab = ({ facility }: FacilityProps) => {
  const isAdmin = useIsAdmin();
  const logic = usePatchFacilityLogic(facility);
  const addUppdragLogic = useAddUppdragFromFacilityPage(facility);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const notification = useNotification();
  // Delete facility
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteFacilityMutation = useMutation({
    ...deleteFacility(),
    onSuccess: () => {
      notification.info(`Anläggning borttagen`);
      qc.invalidateQueries({ queryKey: [CustomerQueryKey.GetOne] });
      navigate({
        to: "/app/kunder/$customerId",
        params: { customerId: (facility?.customer.id ?? -1).toString() },
      });
    },
  });

  const hasRightToEditFacility = useHasRightsForFeature(
    OperatorPrivilege.EditFacility,
  );

  return (
    <ShadowCard className="max-w-5xl">
      <HeaderContainer>
        <Headline4>Översikt</Headline4>
        <div className="flex items-center gap-x-3">
          <PrimaryButton size="small" onClick={addUppdragLogic.openModal}>
            + Nytt uppdrag
          </PrimaryButton>
          {hasRightToEditFacility && (
            <DotMenu
              items={[
                {
                  label: "Redigera",
                  onClick: logic.openModal,
                },
                {
                  label: "Ta bort anläggning",
                  onClick: () => setIsDeleteModalOpen(true),
                  isDelete: true,
                },
              ].filter((x) => x.label !== "Ta bort anläggning" || isAdmin)}
            />
          )}
        </div>
      </HeaderContainer>
      <AddUppdragFromFacilityPageModal logic={addUppdragLogic} />
      <DangerModal
        isOpen={isDeleteModalOpen}
        title={`Ta bort anläggning`}
        actionText="Ta bort anläggning"
        actionHandler={() => deleteFacilityMutation.mutate(facility.id)}
        onClose={() => setIsDeleteModalOpen(false)}
        paragraphs={[`Är du säker på att du vill ta bort anläggningen?`]}
      />
      <ContentContainer>
        <DetailsList>
          <Item>
            <Label>Namn</Label>
            <Value>{facility.name}</Value>
          </Item>
          <Item>
            <Label>Epostadress för rapporter</Label>
            <Value>{facility.documentationEmail}</Value>
          </Item>
          {!!facility.externalId && (
            <Item>
              <Label>ID i externt system</Label>
              <Value>{facility.externalId}</Value>
            </Item>
          )}
          <Item>
            <Label>Adressuppgifter</Label>
            <Value>
              <div className="space-y-1">
                {!!facility.address && <p>{facility.address}</p>}
                {!!facility.postalCode && <p>{facility.postalCode}</p>}
                {!!facility.city && <p>{facility.city}</p>}
              </div>
            </Value>
          </Item>
          <Item>
            <Label>Kommun</Label>
            <Value>
              {facility.kommunKod ? GetKommunLabel(facility.kommunKod) : ""}
            </Value>
          </Item>
          <Item>
            <Label>Koordinater</Label>
            <Value>
              {!!facility.coordinates && (
                <a
                  href={`http://www.google.com/maps/place/${facility.coordinates.replace(
                    " ",
                    "",
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-600 underline"
                >
                  Öppna Google Maps
                </a>
              )}
            </Value>
          </Item>
          <Item>
            <Label>CFAR-nummer</Label>
            <Value>{facility.cfarNr}</Value>
          </Item>
          <Item>
            <Label>Kontakt</Label>
            <Value>
              <div className="space-y-1">
                {!!facility.contactName && <p>{facility.contactName}</p>}
                {!!facility.contactPhone && <p>{facility.contactPhone}</p>}
                {!!facility.contactEmail && <p>{facility.contactEmail}</p>}
              </div>
            </Value>
          </Item>
          <Item>
            <Label>Anteckningar</Label>
            <Value>{facility.notes}</Value>
          </Item>

          <PatchFacilityModal logic={logic} />
        </DetailsList>
      </ContentContainer>
    </ShadowCard>
  );
};
